/**
 * The space's Settings tab: two sections.
 *
 * - **Context**: the space's name and its description, the context the team
 *   writes for the space (`POST /api/amoreg/space`).
 * - **Who has access**: the team (host members, and on a client space only
 *   those who are also members of the client organization, the ethical
 *   wall of `spaceAccess.ts`), the client organization the space serves,
 *   the client's guests with their roles (invite, change, remove), and what
 *   the client's people are shown (one switch per kind of data).
 *
 * Managers edit; everyone else on the team reads. Every write goes to the
 * server, which applies the rules; errors show inline, in the server's
 * words.
 *
 * @module components/spaces/SpaceSettings
 */
import { LockIcon, ShieldCheckIcon, UserPlusIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../state/orgProfile";
import { useOrgs } from "../../state/orgs";
import {
  CLIENT_ACCESS_DENY_ALL,
  useSpaceGuests,
  type ClientAccess,
  type SpaceDetail,
  type SpaceGuestRole,
  type SpaceView,
} from "../../state/spaces";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Avatar, AvatarFallback, AvatarImage, personInitials } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { clientName as clientNameOf, spaceMembers, type SpaceMember } from "./SpacePage.logic";
import {
  SPACE_DESCRIPTION_MAX_LENGTH,
  clientAccessChanges,
  descriptionLength,
  groupClientAccess,
  linkableOrganizations,
  normalizeDescriptionDraft,
  settingExplanation,
  type ClientAccessSetting,
} from "./SpaceSettings.logic";

/* ------------------------------------------------------------------ */
/* Frame                                                               */
/* ------------------------------------------------------------------ */

/** A settings section: its heading and one line on what it governs, then its cards. */
function SettingsSection({
  id,
  title,
  description,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-title`} className="space-y-4" data-space-settings-section={id}>
      <div>
        <h2 id={`${id}-title`} className="text-[15px] font-semibold text-foreground">
          {title}
        </h2>
        <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** One card of settings, as the app's settings pages draw them. */
function Card({
  title,
  description,
  aside,
  marker,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly aside?: ReactNode;
  readonly marker?: "shown" | "withheld";
  readonly children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border/70 bg-card px-4 py-3.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
            {marker ? (
              <span
                aria-hidden
                className={cn("size-1.5 rounded-full", marker === "shown" ? "bg-emerald-500" : "bg-violet-500")}
              />
            ) : null}
            {title}
          </h3>
          {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {aside ? <div className="shrink-0">{aside}</div> : null}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function InlineError({ message }: { readonly message: string | null }) {
  return message ? (
    <p role="alert" className="mt-2 text-xs text-destructive">
      {message}
    </p>
  ) : null;
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

function ContextEditor({
  detail,
  view,
  canManage,
}: {
  readonly detail: SpaceDetail;
  readonly view: SpaceView;
  readonly canManage: boolean;
}) {
  const savedName = detail.space.name;
  const savedDescription = detail.space.description ?? "";
  const [name, setName] = useState(savedName);
  const [description, setDescription] = useState(savedDescription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setName(savedName), [savedName]);
  useEffect(() => setDescription(savedDescription), [savedDescription]);
  const length = descriptionLength(description);
  const tooLong = length > SPACE_DESCRIPTION_MAX_LENGTH;
  const nameChanged = name.trim() !== savedName && name.trim().length > 0;
  const descriptionChanged = normalizeDescriptionDraft(description) !== normalizeDescriptionDraft(savedDescription);
  const dirty = nameChanged || descriptionChanged;
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || tooLong || busy) return;
    setBusy(true);
    setError(null);
    const failure = await view.update({
      ...(nameChanged ? { name: name.trim() } : {}),
      ...(descriptionChanged ? { description: normalizeDescriptionDraft(description) } : {}),
    });
    setBusy(false);
    if (failure) setError(failure);
    else toastManager.add({ type: "success", title: "Space saved" });
  };
  return (
    <form onSubmit={save} className="rounded-xl border border-border/70 bg-card px-4 py-4" data-space-context="">
      <label className="block">
        <span className="text-[13px] font-medium text-foreground">Name</span>
        <Input
          value={name}
          maxLength={60}
          aria-label="Space name"
          disabled={!canManage || busy}
          onChange={(event) => setName(event.currentTarget.value)}
          className="mt-1.5"
        />
      </label>
      <label className="mt-4 block">
        <span className="text-[13px] font-medium text-foreground">Context</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          What this space is for: the client, the engagement, what matters. The agent reads it in every chat filed
          here.
        </span>
        <Textarea
          value={description}
          aria-label="Space context"
          placeholder={
            canManage
              ? "e.g. Whitestar Fund is a Cayman feeder we administer; Q3 NAV and the AML refresh are in flight."
              : "No context yet."
          }
          disabled={!canManage || busy}
          onChange={(event) => setDescription(event.currentTarget.value)}
          className="mt-1.5 min-h-24"
        />
      </label>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className={cn("text-xs tabular-nums", tooLong ? "text-destructive" : "text-muted-foreground")}>
          {length}/{SPACE_DESCRIPTION_MAX_LENGTH}
        </span>
        {canManage ? (
          <Button type="submit" size="sm" disabled={!dirty || tooLong || busy} data-space-context-save="">
            {busy ? "Saving…" : "Save"}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Space admins can change this.</span>
        )}
      </div>
      <InlineError message={error} />
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* People                                                              */
/* ------------------------------------------------------------------ */

function PersonRow({
  name,
  email,
  avatarUrl,
  badge,
  actions,
}: {
  readonly name: string | null;
  readonly email: string;
  readonly avatarUrl?: string | null;
  readonly badge?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <li className="flex min-h-11 items-center gap-3 py-1.5" data-space-person={email}>
      <Avatar className="size-7 text-[10px]">
        {avatarUrl ? <AvatarImage src={avatarUrl} /> : <AvatarFallback>{personInitials({ name, email })}</AvatarFallback>}
      </Avatar>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-foreground">{name ?? email}</span>
        {name ? <span className="block truncate text-xs text-muted-foreground">{email}</span> : null}
      </span>
      {badge}
      {actions}
    </li>
  );
}

function RoleBadge({ children }: { readonly children: ReactNode }) {
  return (
    <span className="shrink-0 rounded-full border border-border/70 px-2 py-0.5 text-[11px] text-muted-foreground">
      {children}
    </span>
  );
}

function TeamCard({ detail, hostOrganizationId }: { readonly detail: SpaceDetail; readonly hostOrganizationId: string | null }) {
  const clientOrganizationId = detail.space.client?.organizationId ?? null;
  const host = useOrgProfile(hostOrganizationId);
  const client = useOrgProfile(clientOrganizationId);
  const hostTeam = host.profile?.team ?? null;
  const clientTeam = clientOrganizationId === null ? null : (client.profile?.team ?? null);
  const loading = host.isPending || (clientOrganizationId !== null && client.isPending);
  const members: ReadonlyArray<SpaceMember> | null =
    hostTeam === null || (clientOrganizationId !== null && clientTeam === null)
      ? null
      : spaceMembers(hostTeam, clientTeam);
  const clientLabel = clientNameOf(detail.space);
  return (
    <Card
      title="Team"
      description={
        clientOrganizationId === null
          ? "Everyone in the organization sees this space and its chats."
          : `Members who are also in ${clientLabel}. A member outside ${clientLabel} does not see this space.`
      }
      aside={members ? <span className="text-xs tabular-nums text-muted-foreground">{members.length}</span> : null}
    >
      {loading ? (
        <ListSkeleton rows={3} />
      ) : members === null ? (
        <p className="text-xs text-muted-foreground">The member list is not available right now.</p>
      ) : members.length === 0 ? (
        <p className="text-xs text-muted-foreground">No one yet.</p>
      ) : (
        <ul className="divide-y divide-border/60" data-space-team="">
          {members.map((member) => (
            <PersonRow
              key={member.id}
              name={member.name}
              email={member.email}
              avatarUrl={member.avatarUrl}
              badge={<RoleBadge>{member.role === "admin" ? "Admin" : "Member"}</RoleBadge>}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

const GUEST_ROLE_LABEL: Readonly<Record<SpaceGuestRole, string>> = {
  viewer: "Can view",
  editor: "Can edit",
};

function GuestRoleSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  readonly value: SpaceGuestRole;
  readonly onChange: (role: SpaceGuestRole) => void;
  readonly disabled?: boolean;
  readonly label: string;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next === "editor" ? "editor" : "viewer")}>
      <SelectTrigger size="sm" className="w-32 min-w-32 shrink-0" aria-label={label} disabled={disabled}>
        <SelectValue>{GUEST_ROLE_LABEL[value]}</SelectValue>
      </SelectTrigger>
      <SelectPopup align="end" alignItemWithTrigger={false}>
        <SelectItem value="viewer">{GUEST_ROLE_LABEL.viewer}</SelectItem>
        <SelectItem value="editor">{GUEST_ROLE_LABEL.editor}</SelectItem>
      </SelectPopup>
    </Select>
  );
}

function GuestsCard({
  detail,
  canManage,
}: {
  readonly detail: SpaceDetail;
  readonly canManage: boolean;
}) {
  const guests = useSpaceGuests(detail.space.id);
  const client = clientNameOf(detail.space);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SpaceGuestRole>("viewer");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canInvite = canManage && detail.clientSpaces;
  const run = async (key: string, write: () => Promise<string | null>, done?: string) => {
    setBusy(key);
    setError(null);
    const failure = await write();
    setBusy(null);
    if (failure) setError(failure);
    else if (done) toastManager.add({ type: "success", title: done });
    return failure === null;
  };
  const invite = async (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address || busy) return;
    if (await run("invite", () => guests.invite(address, role), `${address} added as a guest`)) {
      setEmail("");
    }
  };
  return (
    <Card
      title={`Guests from ${client}`}
      description={`${client}'s people, by email. A guest is never a member of any organization and sees only what is shown to ${client} below.`}
      aside={guests.available ? <span className="text-xs tabular-nums text-muted-foreground">{guests.guests.length}</span> : null}
    >
      {!detail.clientSpaces ? (
        <p className="mb-3 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          Client spaces are not on for this organization, so guests cannot be added yet.
        </p>
      ) : null}
      {guests.isPending ? (
        <ListSkeleton rows={2} />
      ) : !guests.available ? (
        <p className="text-xs text-muted-foreground">
          {guests.failed ? "Guests could not be loaded. Try again in a moment." : "Guests are not available on this server yet."}
        </p>
      ) : guests.guests.length === 0 ? (
        <p className="text-xs text-muted-foreground">No guests yet.</p>
      ) : (
        <ul className="divide-y divide-border/60" data-space-guests="">
          {guests.guests.map((guest) => (
            <PersonRow
              key={guest.email}
              name={null}
              email={guest.email}
              badge={
                canInvite ? (
                  <GuestRoleSelect
                    label={`Role of ${guest.email}`}
                    value={guest.role}
                    disabled={busy !== null}
                    onChange={(next) =>
                      next === guest.role ? undefined : void run(guest.email, () => guests.invite(guest.email, next))
                    }
                  />
                ) : (
                  <RoleBadge>{GUEST_ROLE_LABEL[guest.role]}</RoleBadge>
                )
              }
              actions={
                canManage ? (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Remove ${guest.email}`}
                    disabled={busy !== null}
                    onClick={() => void run(guest.email, () => guests.revoke(guest.email), `${guest.email} removed`)}
                    data-space-guest-remove={guest.email}
                  >
                    <XIcon />
                  </Button>
                ) : null
              }
            />
          ))}
        </ul>
      )}
      {canInvite && guests.available ? (
        <form onSubmit={invite} className="mt-3 flex flex-col gap-2 sm:flex-row" data-space-guest-invite="">
          <Input
            type="email"
            value={email}
            placeholder={`name@${client.toLowerCase().replace(/[^a-z0-9]+/g, "") || "client"}.com`}
            aria-label="Guest email"
            onChange={(event) => setEmail(event.currentTarget.value)}
            disabled={busy !== null}
            className="sm:flex-1"
          />
          <GuestRoleSelect label="Role of the new guest" value={role} onChange={setRole} disabled={busy !== null} />
          <Button type="submit" size="sm" className="sm:self-center" disabled={!email.trim() || busy !== null}>
            <UserPlusIcon />
            {busy === "invite" ? "Adding…" : "Add guest"}
          </Button>
        </form>
      ) : null}
      {guests.available && guests.guests.length > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Guest sign-in is not open yet: a guest added here gets in once it is, with the role shown.
        </p>
      ) : null}
      <InlineError message={error} />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* What the client sees                                                */
/* ------------------------------------------------------------------ */

function SettingRow({
  setting,
  client,
  checked,
  disabled,
  onChange,
}: {
  readonly setting: ClientAccessSetting;
  readonly client: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <label
      className={cn("flex items-center gap-3 rounded-lg py-1.5", disabled ? "cursor-not-allowed" : "cursor-pointer")}
      data-client-access-key={setting.key}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-foreground">{setting.label}</span>
        <span className="block text-xs text-muted-foreground">{settingExplanation(setting, client)}</span>
      </span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={(next) => onChange(next)} />
    </label>
  );
}

/** The two groups of switches and their Save. */
function AccessSettings({
  detail,
  hostShort,
  view,
  canManage,
}: {
  readonly detail: SpaceDetail;
  readonly hostShort: string;
  readonly view: SpaceView;
  readonly canManage: boolean;
}) {
  const saved = detail.clientAccess ?? CLIENT_ACCESS_DENY_ALL;
  const client = clientNameOf(detail.space);
  const [draft, setDraft] = useState<ClientAccess>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A save (or a client change, which resets every key) brings new settings.
  useEffect(() => setDraft(saved), [saved]);
  const groups = groupClientAccess(saved);
  const changes = clientAccessChanges(saved, draft);
  const dirty = Object.keys(changes).length > 0;
  const locked = !canManage || !detail.clientSpaces || busy;
  const save = async () => {
    setBusy(true);
    setError(null);
    const failure = await view.update({ clientAccess: changes });
    setBusy(false);
    if (failure) setError(failure);
    else toastManager.add({ type: "success", title: "Client access saved" });
  };
  const row = (setting: ClientAccessSetting) => (
    <SettingRow
      key={setting.key}
      setting={setting}
      client={client}
      checked={draft[setting.key]}
      disabled={locked}
      onChange={(next) => setDraft((current) => ({ ...current, [setting.key]: next }))}
    />
  );
  return (
    <div className="space-y-3" data-client-access-settings="">
      {!detail.clientSpaces ? (
        <p className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          Client spaces are not on for this organization, so these settings cannot change yet.
        </p>
      ) : null}
      <Card title={`Shown to ${client}`} marker="shown">
        {groups.shown.length === 0 ? (
          <p className="py-1.5 text-xs text-muted-foreground">Nothing yet. {client} sees no part of the program.</p>
        ) : (
          groups.shown.map(row)
        )}
      </Card>
      <Card title={`Withheld from ${client}`} marker="withheld">
        {groups.withheld.map(row)}
        <div className="flex items-center gap-3 py-1.5">
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] text-foreground">{hostShort} chats and internal notes</span>
            <span className="block text-xs text-muted-foreground">Never shown to the client.</span>
          </span>
          <LockIcon aria-label="Always withheld" className="size-3.5 text-muted-foreground" />
        </div>
      </Card>
      {canManage ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes. The groups update when you save." : "Default is deny."}
          </p>
          <div className="flex shrink-0 gap-2">
            {dirty ? (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setDraft(saved)}>
                Discard
              </Button>
            ) : null}
            <Button size="sm" disabled={!dirty || locked} onClick={() => void save()} data-client-access-save="">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : null}
      <InlineError message={error} />
    </div>
  );
}

const NO_CLIENT = "none";

function ClientLink({
  detail,
  hostOrganizationId,
  view,
}: {
  readonly detail: SpaceDetail;
  readonly hostOrganizationId: string | null;
  readonly view: SpaceView;
}) {
  const { orgs } = useOrgs();
  const options = useMemo(() => linkableOrganizations(orgs, hostOrganizationId), [hostOrganizationId, orgs]);
  const current = detail.space.client?.organizationId ?? NO_CLIENT;
  const [choice, setChoice] = useState<string>(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setChoice(current), [current]);
  const linked = detail.space.client !== null;
  const nameOf = (id: string) =>
    id === NO_CLIENT
      ? "No client"
      : (options.find((org) => org.id === id)?.name ??
        (id === detail.space.client?.organizationId ? clientNameOf(detail.space) : "Unknown organization"));
  const apply = async (next: string | null) => {
    setBusy(true);
    setError(null);
    const failure = await view.setClient(next);
    setBusy(false);
    if (failure) setError(failure);
    else toastManager.add({ type: "success", title: next === null ? "Client unlinked" : "Client linked" });
  };
  return (
    <Card
      title="Client organization"
      description="The organization this space serves. Its policies and procedures show on the space's overview; its people can be let in as guests. Changing it turns every client access setting off."
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={choice} onValueChange={(value) => setChoice(typeof value === "string" ? value : NO_CLIENT)}>
          <SelectTrigger className="sm:flex-1" aria-label="Client organization" disabled={busy}>
            <SelectValue>{nameOf(choice)}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="start" alignItemWithTrigger={false}>
            <SelectItem value={NO_CLIENT}>No client</SelectItem>
            {options.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
        <Button
          size="sm"
          variant="outline"
          className="sm:self-center"
          disabled={busy || choice === current || (choice !== NO_CLIENT && !detail.clientSpaces)}
          onClick={() => void apply(choice === NO_CLIENT ? null : choice)}
          data-space-client-apply=""
        >
          {choice === NO_CLIENT && linked ? "Unlink" : linked ? "Change client" : "Link client"}
        </Button>
      </div>
      {!detail.clientSpaces && !linked ? (
        <p className="mt-2 text-xs text-muted-foreground">Client spaces are not on for this organization.</p>
      ) : null}
      <InlineError message={error} />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* The tab                                                             */
/* ------------------------------------------------------------------ */

export function SpaceSettings({
  detail,
  view,
  hostShort,
  hostOrganizationId,
}: {
  readonly detail: SpaceDetail;
  readonly view: SpaceView;
  /** "Apex". */
  readonly hostShort: string;
  readonly hostOrganizationId: string | null;
}) {
  const canManage = detail.access.kind === "staff" && detail.access.canManage;
  const linked = detail.space.client !== null;
  const client = clientNameOf(detail.space);
  return (
    <div className="space-y-12" data-space-settings="">
      <SettingsSection
        id="context"
        title="Context"
        description="The space's name and the context the team and the agent work from."
      >
        <ContextEditor detail={detail} view={view} canManage={canManage} />
      </SettingsSection>
      <SettingsSection
        id="access"
        title="Who has access"
        description={
          linked
            ? `Who works in this space, which of ${client}'s people are let in, and what they see.`
            : "Who works in this space, and the client organization it serves, if any."
        }
      >
        <TeamCard detail={detail} hostOrganizationId={hostOrganizationId} />
        {canManage ? <ClientLink detail={detail} hostOrganizationId={hostOrganizationId} view={view} /> : null}
        {linked ? <GuestsCard detail={detail} canManage={canManage} /> : null}
        {linked ? (
          <div className="space-y-3 pt-2">
            <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
              <ShieldCheckIcon aria-hidden className="size-4 text-muted-foreground" />
              What {client} sees
            </h3>
            <AccessSettings detail={detail} hostShort={hostShort} view={view} canManage={canManage} />
          </div>
        ) : null}
      </SettingsSection>
    </div>
  );
}
